import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import TableCell from '@material-ui/core/TableCell';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Tooltip from '@material-ui/core/Tooltip';
import Skeleton from '@material-ui/lab/Skeleton';

const getColKey = (col, i) => `${col.name}${i}`;

const NodeType = PropTypes.shape({
  sortedChildren: PropTypes.arrayOf(PropTypes.shape({})),
  values: PropTypes.arrayOf(PropTypes.any),
  treeDepth: PropTypes.number,
  collapsed: PropTypes.bool,
  obj: PropTypes.shape({}),
});

const makeColumnClassName = (col, prefix = 'ReactVirtualized__Table__rowColumn_') => {
  const { align, className, name, id, padding } = col;
  const classNames = [`${prefix}${id || name || className}`];
  if (align) {
    classNames.push(`${prefix}${align}`);
  }
  if (padding === 'none') {
    classNames.push(`${prefix}no_padding`);
  }
  switch (classNames.length) {
    case 0: return undefined;
    case 1: return classNames[0];
    default: return classNames.join(' ');
  }
};

const SkeletonCell = (col) => {
  const Cell = ({ key, style }) => (
    <TableCell
      component="div"
      key={key}
      align={col.align || (col.numeric ? 'right' : 'left')}
      padding={col.padding}
      className={classnames(col.numeric && 'tuc-table-cell-numeric', col.tree && 'tuc-table-cell-tree', col.className)}
      variant="body"
      style={style}
    >
      <Skeleton variant="text" />
    </TableCell>
  );

  Cell.propTypes = {
    key: PropTypes.string,
    style: PropTypes.shape({}),
  };

  Cell.defaultProps = {
    key: undefined,
    style: undefined,
  };
  return Cell;
};

const IconCell = (col, i, props) => {
  const CellCmp = col.rendererCmp;
  const { tableHandler } = props;
  let cellProps = props.cellProperties || props;
  if (col.tree) {
    cellProps = {
      ...cellProps,
      tableHandler,
    };
  }
  const className = classnames('grb_virtualized_table_icon_cell', col.tree && 'tuc-table-cell-tree', col.className);
  const Cell = ({ rowData, style, key }) => {
    const { values } = rowData;
    return (
      <div
        style={style}
        className={className}
      >
        {CellCmp && (
          <CellCmp
            {...cellProps}
            {...(!col.tree ? {} : {
              leaf: !rowData.sortedChildren,
              treeDepth: rowData.treeDepth,
              collapsed: rowData.collapsed,
            })}
            key={key}
            value={values[i]}
            object={rowData.obj}
            type={col.type}
            property={col}
          />
        )}
      </div>
    );
  };

  Cell.propTypes = {
    key: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    rowData: NodeType.isRequired,
    style: PropTypes.shape({}),
  };

  Cell.defaultProps = {
    style: undefined,
    key: undefined,
  };

  return Cell;
};

const DefaultCell = (col, i, tableProps) => {
  const { tableHandler } = tableProps;
  const handleCellEdit = (e) => {
    tableHandler.onEditCell(e, col, i);
  };
  let cellProps = tableProps.cellProperties || tableProps;
  if (col.tree) {
    cellProps = {
      ...cellProps,
      tableHandler,
    };
  }
  const numeric = col.type === 'integer' || col.type === 'number';
  const className = classnames(col.numeric && 'tuc-table-cell-numeric', col.tree && 'tuc-table-cell-tree', col.className);
  const editClassName = classnames(className, 'tuc-table-cell-editing');

  const Cell = ({ rowData, style, key }) => {
    const editing = tableProps.editedRow === rowData;
    const errors = editing ? tableProps.validationErrors : undefined;
    const editColumn = editing && (col.editable === undefined || col.editable);
    const CellCmp = editing && col.editorCmp || col.rendererCmp;
    const values = editing ? tableProps.editValues : rowData.values;
    return (
      <TableCell
        component="div"
        key={key}
        align={col.align || (numeric ? 'right' : 'left')}
        padding={col.padding}
        className={editing ? editClassName : className}
        variant="body"
        style={style}
      >
        {CellCmp && (
          <CellCmp
            {...cellProps}
            {...(!col.tree ? {} : {
              leaf: !rowData.sortedChildren,
              treeDepth: rowData.treeDepth,
              collapsed: rowData.collapsed,
            })}
            className={editing ? editClassName : className}
            editing={editing}
            onChange={handleCellEdit}
            value={values[i]}
            error={editColumn && errors && errors[i] || ''}
            object={rowData.obj}
            type={col.type}
            property={col}
          />
        )}
        {!CellCmp && <span>{rowData.fvalues[i]}</span>}
      </TableCell>
    );
  };

  Cell.propTypes = {
    key: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    rowData: NodeType.isRequired,
    style: PropTypes.shape({}),
  };

  Cell.defaultProps = {
    style: undefined,
    key: undefined,
  };

  return Cell;
};

const makeCellRenderer = (col, i, props) => {
  const DefaultRenderer = col.iconColumn ? IconCell(col, i, props) : DefaultCell(col, i, props);
  const SkeletonTableCell = SkeletonCell(col);

  const Cell = ({ rowData, style, key }) => {
    return rowData
      ? <DefaultRenderer rowData={rowData} style={style} key={key} />
      : <SkeletonTableCell style={style} key={key} />;
  };

  Cell.propTypes = {
    key: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    rowData: NodeType.isRequired,
    style: PropTypes.shape({}),
  };

  Cell.defaultProps = {
    style: undefined,
    key: undefined,
  };

  return Cell;
};

export default (col, colIndex, options) => {
  const { canSort = true, iconColumn: iconWidth } = col;
  const numeric = col.type === 'integer' || col.type === 'number';
  const colKey = getColKey(col, colIndex);

  //
  // Header renderer
  //
  const handleSort = () => {
    options.tableHandler.onSort(col);
  };

  const label = col.short ? (
    <span className={`grb-width-${col.short.breakpoint || '1366'}`}>
      <span className="short">{col.short.label}</span>
      <span className="full">{col.label}</span>
    </span>
  ) : (<span>{col.label}</span>);
  const tooltip = col.description || (col.shortLabel && col.label);
  const headerClassName = iconWidth
    ? classnames(makeColumnClassName(col, 'ReactVirtualized__Table__headerColumn_'), 'ReactVirtualized__Table__iconColumn')
    : makeColumnClassName(col, 'ReactVirtualized__Table__headerColumn_');

  const makeHeaderRenderer = ({ sortedColumn, sortOrder }) => {
    return () => (
      <TableCell
        component="div"
        key={colKey}
        align={col.align || (numeric ? 'right' : 'left')}
        className={classnames(
          'grb-virtualized-table-col',
          numeric && 'tuc-table-cell-numeric',
          col.className,
          col.noHeaderWrap && 'grb-virtualized-table-col-no-wrap',
        )}
        variant="head"
      >
        {canSort && (
          <Tooltip
            title={col.description || col.label}
            placement={numeric ? 'bottom-end' : 'bottom-start'}
            enterDelay={300}
          >
            <TableSortLabel
              active={sortedColumn === col}
              direction={sortOrder}
              onClick={handleSort}
            >
              {label}
            </TableSortLabel>
          </Tooltip>
        )}
        {!canSort && !!tooltip && (
          <Tooltip
            title={tooltip}
            placement={numeric ? 'bottom-end' : 'bottom-start'}
            enterDelay={300}
          >
            {label}
          </Tooltip>
        )}
        {!canSort && !col.description && (
          <span>{label}</span>
        )}
      </TableCell>
    );
  };

  //
  // Cell Renderer
  //

  const cellDataGetter = options.tableHandler.isPaging()
    ? ({ rowData }) => (rowData ? rowData.fvalues[colIndex] : '')
    : ({ rowData }) => rowData.fvalues[colIndex];

  const className = iconWidth
    ? classnames(makeColumnClassName(col), 'ReactVirtualized__Table__iconColumn')
    : makeColumnClassName(col);

  const sizing = iconWidth ? {
    width: col.iconColumn === 'small' && 20 || 30,
    minWidth: iconWidth,
    maxWidth: iconWidth,
    flexShrink: 0,
    flexGrow: 0,
  } : {
    width: col.width || (col.numeric ? 120 : 200),
    minWidth: col.minWidth,
    maxWidth: col.maxWidth,
    flexShrink: col.flexShrink !== undefined ? col.flexShrink : (col.minWidth && 1 || 0),
    flexGrow: col.flexGrow !== undefined ? col.flexGrow : (col.maxWidth && 1 || 0),
  };

  const getBreakPointSize = col.breakpoints && (({ width: tableWidth }) => {
    let breakpoint;
    let bestScore = -1;
    col.breakpoints.forEach(({ tableMinWidth, tableMaxWidth, ...b }) => {
      if (tableMinWidth) {
        if (tableMinWidth > tableWidth) {
          return;
        }
        if ((bestScore < 0) || (bestScore > (tableWidth - tableMinWidth))) {
          bestScore = tableWidth - tableMinWidth;
          breakpoint = b;
        }
      }
      if (tableMaxWidth) {
        if (tableMaxWidth < tableWidth) {
          return;
        }
        if ((bestScore < 0) || (bestScore > (tableMaxWidth - tableWidth))) {
          bestScore = tableMaxWidth - tableWidth;
          breakpoint = b;
        }
      }
    });
    return breakpoint ? { ...sizing, ...breakpoint } : sizing;
  });

  const getTreeSize = col.tree && ((props) => {
    const colsize = getBreakPointSize ? getBreakPointSize(props) : sizing;
    const inc = 30 * props.tableDepth + 30; // Add 30 for the collapse/expand button
    return {
      ...sizing,
      width: colsize.width + inc,
      minWidth: colsize.minWidth ? colsize.minWidth + inc : undefined,
      maxWidth: colsize.maxWidth ? colsize.maxWidth + inc : undefined,
    };
  });

  const getSize = getTreeSize || getBreakPointSize;

  // const DataColumn = (props) => {
  //   const { Column } = props;
  //   return (
  //     <Column
        // key={colKey}
        // {...(getSize ? getSize(props) : sizing)}
        // headerClassName={headerClassName}
        // headerRenderer={makeHeaderRenderer(props)}
        // className={className}
        // cellRenderer={makeCellRenderer(props)}
        // cellDataGetter={cellDataGetter}
  //     />
  //   );
  // };

  // DataColumn.propTypes = {
  //   dataKey: PropTypes.oneOfType([
  //     PropTypes.string,
  //     PropTypes.number,
  //   ]).isRequired,
  //   width: PropTypes.number.isRequired,
  // };
  return {
    name: col.name, // debug
    minWidth: (props) => {
      const { minWidth, width } = (getSize ? getSize(props) : sizing);
      return minWidth || width;
    },
    generateProps: (props) => ({
      key: colKey,
      ...(getSize ? getSize(props) : sizing),
      headerClassName,
      headerRenderer: makeHeaderRenderer(props),
      className,
      cellRenderer: makeCellRenderer(col, colIndex, props),
      cellDataGetter,
      dataKey: colIndex,
    }),
  };
};
